'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { studioClient } from '@/sanity/lib/client';
import { localizedOurValuesQuery } from '@/sanity/queries/ourValues';
import { IOurValues } from '@/types/ourValues';
import { LoadingSpinner } from '@/shared';

interface ValueCardProps {
  title: string;
  description: string;
  icon?: {
    asset: {
      _id: string;
      url: string;
      metadata: {
        dimensions: {
          width: number;
          height: number;
        };
      };
    };
    alt?: string;
  };
}

const ValueCard = ({ title, description, icon }: ValueCardProps) => {
  return (
    <div className="flex flex-col items-center justify-center gap-3 sm:gap-4">
      <div
        className="h-[70px] w-[70px] rounded-[18px] border border-[#7FB3A8] p-4 sm:h-[88px] sm:w-[88px] sm:rounded-[23px] sm:p-6"
        style={{
          background:
            'linear-gradient(180deg, rgba(23, 146, 99, 0.74) 0%, rgba(20, 5, 39, 0.27) 100%)',
        }}>
        {icon && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative h-8 w-8 sm:h-12 sm:w-12">
              <Image
                src={icon.asset.url}
                alt={icon.alt || title}
                fill
                className="object-contain"
                sizes="(max-width: 640px) 32px, 48px"
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col items-center justify-center text-center">
        <h3 className="mb-1 text-lg font-bold text-white sm:text-xl lg:text-2xl">{title}</h3>
        <p className="max-w-xs text-sm leading-relaxed text-[#D2D7E0B2] sm:max-w-sm sm:text-base lg:max-w-md lg:text-lg xl:text-xl">
          {description}
        </p>
      </div>
    </div>
  );
};

export default function OurValues() {
  const locale = useLocale();
  const [ourValuesData, setOurValuesData] = useState<IOurValues | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOurValues = async () => {
      try {
        const data = await studioClient.fetch<IOurValues>(localizedOurValuesQuery(locale));
        setOurValuesData(data);
      } catch (error) {
        console.error('Error fetching our values data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOurValues();
  }, [locale]);

  if (loading) {
    return (
      <section className="relative px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-white/70">Loading our values...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!ourValuesData) {
    return (
      <section className="relative px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <p className="text-white/70">Our Values section is not available</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const values = ourValuesData.values;

  const BackgroundBlur = ({
    position,
  }: {
    position:
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'
      | 'center-left'
      | 'center-right'
      | 'center';
  }) => {
    const getPositionStyles = () => {
      switch (position) {
        case 'top-left':
          return { left: '-20%', top: '0%' };
        case 'top-right':
          return { right: '-20%', top: '0%' };
        case 'bottom-left':
          return { left: '-20%', bottom: '0%' };
        case 'bottom-right':
          return { right: '-20%', bottom: '0%' };
        case 'center-left':
          return { left: '-20%', top: '50%', transform: 'translateY(-50%)' };
        case 'center-right':
          return { right: '-20%', top: '50%', transform: 'translateY(-50%)' };
        case 'center':
          return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
        default:
          return { left: '-20%', top: '0%' };
      }
    };

    return (
      <div
        className="hidden lg:block"
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          opacity: 0.2,
          background: 'rgba(32, 255, 36, 0.71)',
          filter: 'blur(161.04000854492188px)',
          zIndex: 0,
          pointerEvents: 'none',
          ...getPositionStyles(),
        }}
        aria-hidden="true"
      />
    );
  };

  return (
    <section className="relative px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Background Blur Effects */}
        <BackgroundBlur position="center-left" />
        <BackgroundBlur position="center-right" />
        <BackgroundBlur position="center" />

        {/* Section Header */}
        <div className="mb-8 text-center sm:mb-12 lg:mb-16">
          <h2 className="text-hero-gradient-text mb-4 text-3xl font-bold sm:text-4xl lg:text-5xl">
            {ourValuesData.title}
          </h2>
          {ourValuesData.description && (
            <p className="mx-auto max-w-3xl text-base text-white/80 sm:text-lg lg:text-xl">
              {ourValuesData.description}
            </p>
          )}
        </div>

        {/* Mobile Layout - Stacked Cards */}
        <div className="block space-y-8 lg:hidden">
          {values.map((value) => (
            <div key={value.position} className="flex justify-center">
              <ValueCard title={value.title} description={value.description} icon={value.icon} />
            </div>
          ))}
        </div>

        {/* Desktop Layout - 5 Cards with Center */}
        <div className="relative hidden min-h-[600px] items-center justify-center lg:flex">
          {/* Connecting Lines */}
          <div className="absolute inset-0 -z-50 mt-5 flex items-center justify-center">
            {/* Horizontal lines */}
            <div className="absolute h-px w-full bg-gradient-to-r from-transparent via-[#237A40]/20 to-transparent"></div>
            {/* Vertical lines */}
            <div className="absolute h-full w-px bg-gradient-to-b from-transparent via-[#237A40]/20 to-transparent"></div>
          </div>

          {/* Center Card */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <ValueCard
              title={values.find((v) => v.position === 'center')?.title || ''}
              description={values.find((v) => v.position === 'center')?.description || ''}
              icon={values.find((v) => v.position === 'center')?.icon}
            />
          </div>

          {/* Corner Cards */}
          <div className="absolute top-0 left-0">
            <ValueCard
              title={values.find((v) => v.position === 'top-left')?.title || ''}
              description={values.find((v) => v.position === 'top-left')?.description || ''}
              icon={values.find((v) => v.position === 'top-left')?.icon}
            />
          </div>

          <div className="absolute top-0 right-0">
            <ValueCard
              title={values.find((v) => v.position === 'top-right')?.title || ''}
              description={values.find((v) => v.position === 'top-right')?.description || ''}
              icon={values.find((v) => v.position === 'top-right')?.icon}
            />
          </div>

          <div className="absolute bottom-0 left-0">
            <ValueCard
              title={values.find((v) => v.position === 'bottom-left')?.title || ''}
              description={values.find((v) => v.position === 'bottom-left')?.description || ''}
              icon={values.find((v) => v.position === 'bottom-left')?.icon}
            />
          </div>

          <div className="absolute right-0 bottom-0">
            <ValueCard
              title={values.find((v) => v.position === 'bottom-right')?.title || ''}
              description={values.find((v) => v.position === 'bottom-right')?.description || ''}
              icon={values.find((v) => v.position === 'bottom-right')?.icon}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
